import { Button, ImageIcon, styled, SyncIcon } from 'canoe';
import React, { ChangeEvent, useState } from 'react';

import { TextButton, ValueBox, ValueGroup, ValueInput, ValueToggle } from '@candu-ai/craft/ui';
import { useToggle } from 'commons/hooks';
import { MediaLibrary } from 'craft/components/MediaLibrary';
import { VariableHelp, VariableSummary } from 'craft/components/ToolboxVariable';
import {
  getCssForBackgroundImg,
  sanitizeBackgroundImage,
} from 'craft/containers/Toolbox/sections/Background/utils';
import { useCss } from 'craft/hooks';

const StyledButton = styled(Button)`
  height: 32px;
`;

interface Props {
  nodeIds: string[];
}

export const ThumbnailField = ({ nodeIds }: Props) => {
  const [isModalOpen, toggleModal] = useToggle(false);

  const { css, setCss } = useCss(nodeIds);

  const { backgroundImage } = css;
  const sanitizedImage = backgroundImage && sanitizeBackgroundImage(backgroundImage);

  const onResetBackground = () => setCss(getCssForBackgroundImg());

  const [activeTab, setActiveTab] = useState<string>(() => {
    if (sanitizedImage && !sanitizedImage.toLowerCase().includes('media.candulabs.com')) {
      return 'url';
    }
    return 'upload';
  });

  const imageName = sanitizedImage
    ? sanitizedImage.match(/\/?([\w-]+\..{3})(?=\?|$)/)?.[1] || null
    : null;

  return (
    <>
      <ValueGroup>
        <ValueBox responsiveToggle>
          <ValueToggle
            toggleGroup="media-upload"
            label="Upload"
            isActive={activeTab === 'upload'}
            onClick={() => setActiveTab('upload')}
          />
          <ValueToggle
            toggleGroup="media-upload"
            label="Insert from URL"
            isActive={activeTab === 'url'}
            onClick={() => setActiveTab('url')}
          />
        </ValueBox>
      </ValueGroup>
      {activeTab === 'upload' && (
        <>
          <ValueGroup>
            {imageName || backgroundImage ? (
              <>
                <StyledButton
                  fullWidth
                  appearance="outline"
                  variant="neutral"
                  size="xsmall"
                  icon={SyncIcon}
                  onClick={toggleModal}
                >
                  Replace Image
                </StyledButton>
                <ValueGroup mt="xs">
                  <ValueBox label="Image Name">
                    <ValueInput isReadOnly value={imageName || backgroundImage || ''} />
                  </ValueBox>
                </ValueGroup>
                <TextButton onClick={onResetBackground}> Reset</TextButton>
              </>
            ) : (
              <StyledButton
                fullWidth
                appearance="outline"
                variant="neutral"
                size="xsmall"
                icon={ImageIcon}
                onClick={toggleModal}
              >
                Choose Image
              </StyledButton>
            )}
            <MediaLibrary
              nodeIds={nodeIds}
              name="background"
              isModalOpen={isModalOpen}
              toggleModal={toggleModal}
            />
          </ValueGroup>
        </>
      )}
      {activeTab === 'url' && (
        <>
          <ValueGroup>
            <ValueBox label="Image URL">
              <ValueInput
                value={sanitizedImage || ''}
                placeholder="https://"
                onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                  if (!value) {
                    setCss(getCssForBackgroundImg());
                  } else {
                    setCss(getCssForBackgroundImg(value));
                  }
                }}
              />
            </ValueBox>
          </ValueGroup>
          {sanitizedImage ? <VariableSummary url={sanitizedImage} /> : null}
          <VariableHelp />
        </>
      )}
    </>
  );
};
